import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DealPeopleResult } from '@whinerp/client';
import { Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Who is on this deal, theirs and ours (G-034, G-086).
 *
 * The share total is shown whether or not it is a hundred, and marked when it
 * is not. The server refuses a save that does not total a hundred, but a rep
 * halfway through typing needs to see where they are - a panel that only spoke
 * at save time would leave them adding percentages in their head.
 *
 * A contact's role is what they are TO THE DEAL rather than their job title.
 * "Have we met the person who signs?" is the question the field exists for,
 * and it can only be asked of comparable values.
 */
export function DealPeoplePanel({ dealId }: { dealId: string }): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const people = useQuery<DealPeopleResult, ApiError>({
    queryKey: ['deal-people', dealId],
    queryFn: () => api.dealPeople({ path: { dealId } }),
  });

  const setRole = useMutation<unknown, ApiError, { contactId: string; role: string }>({
    mutationFn: (input) =>
      api.setDealContact({
        path: { dealId, contactId: input.contactId },
        body: { role: input.role as 'champion' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deal-people', dealId] }),
  });

  if (people.isPending) return <Skeleton className="h-block-sm w-full" />;
  // Quiet on failure: this sits beside the deal rather than being it.
  if (people.isError) return <></>;

  const team = people.data.team;
  const total = team.reduce((sum, member) => sum + member.splitPercent, 0);

  return (
    <Panel data-region="deal-people">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('deal.people.theirs')}</SectionLabel>
          {setRole.error ? (
            <p data-people-refusal className="text-dense text-danger-text">
              {setRole.error.message}
            </p>
          ) : null}
          {people.data.contacts.length === 0 ? (
            <p className="text-meta text-ink-meta">{t('deal.people.noContacts')}</p>
          ) : (
            <ul className="flex flex-col">
              {people.data.contacts.map((contact) => (
                <li
                  key={contact.contactId}
                  data-deal-contact={contact.contactId}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">{contact.name}</span>
                  <label className="flex items-center gap-1">
                    <span className="sr-only">
                      {t('deal.people.roleFor', { values: { name: contact.name } })}
                    </span>
                    <select
                      data-contact-role={contact.contactId}
                      value={contact.role}
                      disabled={setRole.isPending}
                      onChange={(event) =>
                        setRole.mutate({ contactId: contact.contactId, role: event.target.value })
                      }
                      className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                    >
                      {(
                        ['decision-maker', 'champion', 'blocker', 'influencer', 'user', 'technical'] as const
                      ).map((role) => (
                        <option key={role} value={role}>
                          {t(`deal.people.${role}` as 'deal.people.champion')}
                        </option>
                      ))}
                    </select>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <SectionLabel>{t('deal.people.ours')}</SectionLabel>
          {team.length === 0 ? (
            // An empty team is a real state: the deal is the owner's alone,
            // which is most deals.
            <p className="text-meta text-ink-meta">{t('deal.people.noTeam')}</p>
          ) : (
            <>
              <ul className="flex flex-col">
                {team.map((member) => (
                  <li
                    key={member.userId}
                    data-deal-team={member.userId}
                    className="flex flex-wrap items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-body text-ink">{member.name}</span>
                    <span className="text-meta text-ink-meta">
                      {t(`deal.people.${member.role}` as 'deal.people.owner')}
                    </span>
                    <Num data-team-split={member.userId} className="text-dense text-ink">
                      {String(member.splitPercent)}%
                    </Num>
                  </li>
                ))}
              </ul>
              {/*
                The total, said out loud.

                The server refuses a save that does not come to a hundred, but
                a rep halfway through needs to see where they are - a panel
                that only spoke at save time would leave them adding
                percentages in their head and meeting a refusal at the end.
              */}
              <p
                data-team-total={total}
                className={
                  total === 100
                    ? 'flex justify-between text-meta text-ink-meta'
                    : 'flex justify-between text-meta text-danger-text'
                }
              >
                <span>{t('deal.people.total')}</span>
                <Num>{`${String(total)}%`}</Num>
              </p>
            </>
          )}
        </div>
      </div>
    </Panel>
  );
}
